import storage from 'redux-persist/lib/storage';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import { setupListeners } from '@reduxjs/toolkit/query';
import { reducer as toastrReducer } from 'react-redux-toastr';

import { oidcReducer } from '@britishcouncil/react-oidc-auth';

import basketReducer from './basket/basketSlice';
import prsReducer from './post-results-services/prsSlice';
import candidateReducer from './candidate/candidateSlice';
import countryReducer from './country/countrySlice';
import examsSearchReducer from './exams-search/examsSearchSlice';
import featuresSlice from './features/featuresSlice';
import userReducer from './user/userSlice';
import { basketApi, teachingGroupApi } from './basket/basketAPI';
import { candidateApi } from './candidate/candidateAPI';
import { childCollectionOptionsApi } from './child-collection-option/childCollectionOptionAPI';
import { countriesApi } from './country/countryAPI';
import { examsSearchApi } from './exams-search/examsSearchAPI';
import { familyRelationshipsApi } from './family-relationship/familyRelationshipAPI';
import { featuresApi } from './features/featuresAPI';
import { idTypesApi } from './idType/idTypeAPI';
import { myRegistrationsApi } from './my-registrations/myRegistrationsAPI';
import { inboxApi } from './inbox/inboxApi';
import { postResultsServicesApi } from './post-results-services/postResultsServicesApi';
import { paymentApi } from './payment/paymentAPI';
import { signupApi } from './signup/signupAPI';
import { termsApi } from './terms-and-conditions/termsAPI';
import { userApi } from './user/userAPI';
import { organisationCountryApi } from './organisation-country/organisation-country-API';

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['basket', 'country', 'candidate'],
};

export interface EnvelopeResult<T> {
  Data: T;
  IsFailure: boolean;
  IsSuccess: boolean;
}

export const rootReducer = combineReducers({
  oidc: oidcReducer,
  toastr: toastrReducer,
  country: countryReducer,
  basket: basketReducer,
  prs: prsReducer,
  features: featuresSlice,
  exams: examsSearchReducer,
  candidate: candidateReducer,
  user: persistReducer(
    { key: 'user', storage, whitelist: ['reLoginRequired', 'user', 'afterLoginState'] },
    userReducer
  ),
  [countriesApi.reducerPath]: countriesApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [featuresApi.reducerPath]: featuresApi.reducer,
  [signupApi.reducerPath]: signupApi.reducer,
  [termsApi.reducerPath]: termsApi.reducer,
  [examsSearchApi.reducerPath]: examsSearchApi.reducer,
  [basketApi.reducerPath]: basketApi.reducer,
  [teachingGroupApi.reducerPath]: teachingGroupApi.reducer,
  [candidateApi.reducerPath]: candidateApi.reducer,
  [familyRelationshipsApi.reducerPath]: familyRelationshipsApi.reducer,
  [childCollectionOptionsApi.reducerPath]: childCollectionOptionsApi.reducer,
  [idTypesApi.reducerPath]: idTypesApi.reducer,
  [paymentApi.reducerPath]: paymentApi.reducer,
  [myRegistrationsApi.reducerPath]: myRegistrationsApi.reducer,
  [organisationCountryApi.reducerPath]: organisationCountryApi.reducer,
  [inboxApi.reducerPath]: inboxApi.reducer,
  [postResultsServicesApi.reducerPath]: postResultsServicesApi.reducer,
});

export const store = configureStore({
  reducer: persistReducer(persistConfig, rootReducer),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    })
      .concat(countriesApi.middleware)
      .concat(userApi.middleware)
      .concat(featuresApi.middleware)
      .concat(basketApi.middleware)
      .concat(signupApi.middleware)
      .concat(termsApi.middleware)
      .concat(examsSearchApi.middleware)
      .concat(candidateApi.middleware)
      .concat(familyRelationshipsApi.middleware)
      .concat(childCollectionOptionsApi.middleware)
      .concat(idTypesApi.middleware)
      .concat(paymentApi.middleware)
      .concat(myRegistrationsApi.middleware)
      .concat(organisationCountryApi.middleware)
      .concat(inboxApi.middleware)
      .concat(postResultsServicesApi.middleware),
});

export const persistor = persistStore(store);

setupListeners(store.dispatch);
